import React from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import { Stack, Typography, IconButton, AvatarGroup, Avatar, Box } from "@mui/material";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";

import { DealStageItem, UserLight } from "store/types";
import { TooltipDiv } from "components/tooltips/TooltipDiv";
import { useNavigate } from "hooks";
import { ActionRoleGuard } from "components/guards/Action-role.guard";
import { RoleName } from "../../../constants";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { useAllowDealTabOpenSelector } from "store";

interface Props extends DealStageItem {
  draggableProvided: DraggableProvided;
  isDragging: boolean;
  stageId: number;
  saveNote: (anchor: HTMLButtonElement) => void;
}

export const Card: React.FC<Props> = ({
  id,
  title,
  asset_class,
  shared_with,
  draggableProvided,
  isDragging,
  saveNote,
  deal_note
}) => {
  const allowNewTab = useAllowDealTabOpenSelector();
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest(".MuiAvatar-root")) {
      return;
    }

    // Don't navigate if currently dragging
    if (isDragging) return;

    navigate({ route: "deal", params: { deal_id: String(id) } });
  };

  return (
    <NavigateLink allowNewTab={allowNewTab} route="deal" params={{ deal_id: String(id) }}>
    <div
      ref={(node) => {
        if (typeof draggableProvided.innerRef === "function") {
          draggableProvided.innerRef(node);
        } else if (draggableProvided.innerRef) {
          (draggableProvided.innerRef as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      }}
      {...draggableProvided.draggableProps}
      {...draggableProvided.dragHandleProps}
      style={{
        ...draggableProvided.draggableProps.style,
        userSelect: "none",
        WebkitUserSelect: "none",
        msUserSelect: "none",
        MozUserSelect: "none",
        WebkitTouchCallout: "none",
        cursor: "grab",
        touchAction: "none"
      }}
      onClick={handleClick}
    >
      <Stack
        gap="15px"
        sx={{
          bgcolor: "#0a31420d",
          borderRadius: 8,
          p: "10px",
          cursor: "grab",
          userSelect: "none",
          WebkitUserSelect: "none",
          msUserSelect: "none",
          MozUserSelect: "none",
          "&:active": {
            cursor: "grabbing"
          },
          "& *": {
            userSelect: "none",
            WebkitUserSelect: "none",
            msUserSelect: "none",
            MozUserSelect: "none",
            pointerEvents: "none"
          },
          "& button": {
            pointerEvents: "auto"
          },
          "& .MuiAvatar-root": {
            pointerEvents: "auto"
          }
        }}
      >
        <Stack sx={{ gap: "4px", pointerEvents: "none" }}>
          <Typography
            sx={{
              lineHeight: 1,
              fontSize: "15px",
              color: "#0a3142",
              whiteSpace: "normal",
              wordBreak: "break-word"
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              lineHeight: 1,
              fontSize: "13px",
              color: "#5d7782",
              whiteSpace: "normal",
              wordBreak: "break-word"
            }}
          >
            {asset_class}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ pointerEvents: "none" }}>
          <ActionRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                saveNote(event.currentTarget);
              }}
              sx={{ p: 0, pointerEvents: "auto" }}
            >
              <StickyNote2OutlinedIcon sx={{ fontSize: "18px", color: deal_note?.id ? "#0a3142" : "#5d7782" }} />
            </IconButton>
          </ActionRoleGuard>
          <AvatarGroup
            sx={{
              "& .MuiAvatar-root": { width: 20, height: 20, fontSize: 12, padding: 0, pointerEvents: "auto" }
            }}
            max={3}
          >
            {shared_with.length &&
              shared_with.map((share: UserLight) => (
                <Box key={share.id}>
                  <TooltipDiv place="bottom" tooltipMessage={share.full_name}>
                    <Avatar src={share.profile_photo_url} />
                  </TooltipDiv>
                </Box>
              ))}
          </AvatarGroup>
        </Stack>
      </Stack>
    </div>
    </NavigateLink>
  );
};
